/**
 * "Comandos de fora" in the left menu (owner, 09/10/2026): the base of Vephus — what is sent from the web or the phone
 * reaches the person's agents — on by default and with no time limit, one click to switch off or on again. Switching
 * on asks for the password when the last sign-in is not recent (it opens a line to the person's computer); switching off
 * is immediate. The full screen (machines, the thread, keys) stays in Agentes › Comandos de fora.
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { RadioTower } from 'lucide-react'
import { api, ApiError } from '../lib/api'
import { useI18n } from '../lib/i18n'
import { ConfirmIdentity } from './ConfirmIdentity'

export function RemoteSwitch() {
  const { t } = useI18n()
  const queryClient = useQueryClient()
  const { data, isError } = useQuery({ queryKey: ['remote'], queryFn: api.remote.overview, refetchInterval: 60_000, retry: false })
  const [confirming, setConfirming] = useState(false)
  const toggle = useMutation({
    mutationFn: (on: boolean) => api.remote.setEnabled(on),
    onSuccess: () => {
      setConfirming(false)
      void queryClient.invalidateQueries({ queryKey: ['remote'] })
    },
    onError: (error, on) => {
      if (on && error instanceof ApiError && error.code === 'reauth_required') setConfirming(true)
    },
  })

  // Somebody with no developer profile (or a server that says no): nothing to switch here.
  if (isError || !data) return null
  const on = data.allowed && data.enabled
  const title = !data.allowed ? t('remote.notAllowed') : on ? t('remote.sidebarOnHint') : t('remote.sidebarOffHint')

  return (
    <>
      <label className={`sidebar__remote${on ? ' is-on' : ''}`} title={title}>
        <input
          type="checkbox"
          checked={on}
          disabled={!data.allowed || toggle.isPending}
          onChange={(event) => toggle.mutate(event.target.checked)}
          aria-describedby="sidebar-remote-hint"
        />
        <RadioTower size={14} aria-hidden />
        <span className="sidebar__remote-label">{t('remote.title')}</span>
        <span id="sidebar-remote-hint" className="sr-only">{title}</span>
      </label>
      {confirming && <ConfirmIdentity onConfirmed={() => toggle.mutate(true)} onCancel={() => setConfirming(false)} />}
    </>
  )
}
